import Login from '@/components/familyPortal/rootComponent/login/Login.vue';
import SignIn from '@/components/familyPortal/rootComponent/signIn/SignIn.vue';
import Regiter from '@/components/familyPortal/rootComponent/registerWithEmail/RegisterWithEmail.vue';
import SignUp from '@/components/familyPortal/rootComponent/signUp/SignUp.vue';
import ApplicationEmptyState from '@/components/familyPortal/applicationEmptyState/ApplicationEmptyState.vue';
import RootComponent from '@/components/familyPortal/rootComponent/RootComponent.vue';
import Home from '../views/Home.vue';
import APP_CONST from '@/constants/AppConst';
import APP_UTILITIES from '@/utilities/commonFunctions';
import CatalogContainer from '@/components/familyPortal/catalogContainer/CatalogContainer.vue';
import cart from '@/store/modules/checkout/cart/module';
import { Route, RawLocation } from 'vue-router';
import { getInstance } from '@/auth';
import CallbackAuthComponent from '@/auth/Callback/CallbackAuth.vue';
import Logout from '@/auth/Callback/Logout.vue';

function userLoggedIn() {
  const { isAuthenticated } = getInstance();
  return isAuthenticated;
}

/**
 * A utility to check if a user is authenticated before accessing the given Vue `Route`, and if not, construct and return a
 * `RawLocation` for the registration route. This `RawLocation` also includes another route to redirect to upon successful authentication on the regisration route,
 * passed as a `query` param called `redirect`.
 * @param to The original destination `Route`. If the user is unauthenticated and no `redirectOverride` is specified, the `path`
 * property of this `to` `Route` serves as the `redirect` path to reroute the user to upon successfully authenticating on registration.
 * @param redirectOverride Optionally override the `redirect` query param path with the provided string instead of using the default from the original `to.path`.
 * @returns If the user is not authenticated, it will construct and return a `RawLocation` pointing to the registration route with a
 * `redirect` query param to redirect the user to the given path upon successful authentication at registration. Otherwise, if the user is already
 * authenticated, returns nothing.
 */
function routeForAuthentication(to: Route, redirectOverride?: string): RawLocation | void {
  if (!userLoggedIn()) {
    return {
      ...APP_CONST.APP_ROUTES.REGISTER,
      query: {
        redirect: redirectOverride || to.path,
      },
    };
  }
}

function preserveRedirect(to: Route, from: Route, next: any) {
  if ('redirect' in from.query && !('redirect' in to.query)) {
    next({ ...to, query: { ...to.query, redirect: from.query.redirect } });
  } else {
    next();
  }
}

function lazyLoad(view: any) {
  return () => import(`@/${view}.vue`);
}

const routes = [
  {
    path: APP_CONST.APP_ROUTES.FAMILY_PORTAL.path,
    name: APP_CONST.APP_ROUTES.FAMILY_PORTAL.name,
    component: RootComponent,
    children: [
      {
        path: '',
        redirect: APP_CONST.APP_ROUTES.LOGIN.path,
      },
      {
        path: '/logout',
        component: Logout,
      },
      {
        path: '/callback',
        component: CallbackAuthComponent,
      },
      {
        path: APP_CONST.APP_ROUTES.REGISTER.path,
        name: APP_CONST.APP_ROUTES.REGISTER.name,
        component: Regiter,
        meta: { name: 'Register', gtm: 'Register' },
        beforeEnter: (
          _to: Route,
          _from: Route,
          next: (redirectPath?: string | undefined) => void
        ) => {
          const hasOldApplicationLink =
            (_to.query && !!_to.query.aid) || (_to.query && !!_to.query.id);

          if (hasOldApplicationLink) {
            next(APP_CONST.APP_ROUTES.PROGRAMS.path);
          } else {
            next();
          }
        },
      },
      {
        path: APP_CONST.APP_ROUTES.SIGNUP.path,
        name: APP_CONST.APP_ROUTES.SIGNUP.name,
        component: SignUp,
        props: (route: Route) => ({ redirect: route.query.redirect }),
        beforeEnter: preserveRedirect,
        meta: { name: 'SignUp', gtm: 'SignUp' },
      },
      {
        path: APP_CONST.APP_ROUTES.LOGIN.path,
        name: APP_CONST.APP_ROUTES.LOGIN.name,
        component: Login,
        props: (route: Route) => ({ redirect: route.query.redirect }),
        beforeEnter: (
          to: Route,
          from: Route,
          next: (redirectPath?: string) => void
        ) => {
          const isLogout = to.query.type === 'logout';
          if (isLogout) {
            cart.RESET_CART();
          }
          preserveRedirect(to, from, next);
        },
        meta: { name: 'Login', gtm: 'Login' },
      },
      {
        path: APP_CONST.APP_ROUTES.SIGNIN.path,
        name: APP_CONST.APP_ROUTES.SIGNIN.name,
        component: SignIn,
        props: (route: Route) => ({ redirect: route.query.redirect }),
        beforeEnter: preserveRedirect,
        meta: { name: 'SignIn', gtm: 'SignIn' },
      },
    ],
  },
  {
    path: APP_CONST.APP_ROUTES.PROGRAMS.path,
    name: APP_CONST.APP_ROUTES.PROGRAMS.name,
    component: CatalogContainer,
    meta: { name: 'PROGRAM CATALOG', gtm: 'Program Catalog' },
  },
  {
    path: `${APP_CONST.APP_ROUTES.SESSION_DETAILS.path}/:sessionId`,
    name: APP_CONST.APP_ROUTES.SESSION_DETAILS.name,
    component: lazyLoad(
      'components/familyPortal/sessionDetail/SessionDetail'
    ),
    props: {
      subHeaderTitle: 'wooo',
      returnTo: '/test',
      path: '',
      subHeader: true,
      bgColor: ''
    },
    meta: {
      name: APP_CONST.APP_ROUTES.SESSION_DETAILS.name,
      gtm: 'Session Details'
    }
  },
  {
    path: APP_CONST.APP_ROUTES.CHECKOUT.path,
    component: lazyLoad('components/familyPortal/checkout/Checkout'),
    meta: { name: 'CHECKOUT', gtm: 'Checkout' },
    children: [
      {
        path: '',
        redirect: APP_CONST.APP_ROUTES.CHECKOUT_REVIEW.path,
      },
      {
        path: APP_CONST.APP_ROUTES.CHECKOUT_REVIEW.path,
        name: APP_CONST.APP_ROUTES.CHECKOUT_REVIEW.name,
        component: lazyLoad(
          'components/familyPortal/checkout/steps/CheckoutReview'
        ),
        meta: { name: 'CHECKOUT', gtm: 'Checkout' },
      },
      {
        path: APP_CONST.APP_ROUTES.CHECKOUT_APPLY.path,
        name: APP_CONST.APP_ROUTES.CHECKOUT_APPLY.name,
        beforeEnter: (to: Route, _from: Route, next: any) => {
          const authenticationCheck = routeForAuthentication(to, APP_CONST.APP_ROUTES.CHECKOUT_REVIEW.path);
          if (authenticationCheck) {
            next(authenticationCheck);
          } else if (cart.isCartEmpty) {
            next(APP_CONST.APP_ROUTES.CHECKOUT_REVIEW);
          } else {
            next();
          }
        },
        component: lazyLoad(
          'components/familyPortal/checkout/steps/CheckoutApply'
        ),
        meta: { name: 'CHECKOUT', gtm: 'Checkout' },
      },
      {
        path: APP_CONST.APP_ROUTES.CHECKOUT_SUBMIT.path,
        name: APP_CONST.APP_ROUTES.CHECKOUT_SUBMIT.name,
        beforeEnter: (to: Route, _from: Route, next: any) => {
          const authenticationCheck = routeForAuthentication(to);
          if (authenticationCheck) {
            next(authenticationCheck);
          } else if (cart.isCartEmpty) {
            next(APP_CONST.APP_ROUTES.CHECKOUT_REVIEW);
          } else if (!cart.applicationsComplete) {
            next(APP_CONST.APP_ROUTES.CHECKOUT_APPLY);
          } else {
            next();
          }
        },
        component: lazyLoad(
          'components/familyPortal/checkout/steps/CheckoutSubmit'
        ),
        meta: { name: 'CHECKOUT', gtm: 'Checkout' },
      },
      {
        path: APP_CONST.APP_ROUTES.CHECKOUT_DONE.path,
        name: APP_CONST.APP_ROUTES.CHECKOUT_DONE.name,
        beforeEnter: (to: Route, _from: Route, next: any) => {
          const authenticationCheck = routeForAuthentication(to);
          if (authenticationCheck) {
            next(authenticationCheck);
          } else if (
            !cart.submittedCart ||
            !cart.submittedCart.cartItems.length
          ) {
            next(APP_CONST.APP_ROUTES.CHECKOUT_REVIEW);
          } else {
            next();
          }
        },
        component: lazyLoad(
          'components/familyPortal/checkout/steps/CheckoutDone'
        ),
        meta: { name: 'CHECKOUT', gtm: 'Checkout' },
      },
    ],
  },
  {
    path: '/',
    name: 'Home',
    component: Home,
    beforeEnter: (to: any, from: any, next: any) => {
      if (userLoggedIn()) {
        next();
      } else {
        next({
          path: APP_CONST.APP_ROUTES.LOGIN.path,
          query: { redirect: to.fullPath },
        });
      }
    },
    children: [
      {
        path: '',
        redirect: APP_CONST.APP_ROUTES.DASHBOARD.path,
      },
      {
        path: APP_CONST.APP_ROUTES.FAMILY_PORTAL.path,
        name: APP_CONST.APP_ROUTES.DASHBOARD.name,
        component: lazyLoad(
          'components/familyPortal/dashboardContainer/DashboardContainer'
        ),
        children: [
          {
            path: '/',
            redirect: APP_CONST.APP_ROUTES.DASHBOARD.path,
          },
          {
            path: APP_CONST.APP_ROUTES.DASHBOARD.path,
            name: 'Family Portal',
            component: lazyLoad(
              'components/familyPortal/dashboardContainer/dashboardMainContent/DashboardMainContent'
            ),
            props: {
              subHeaderTitle: '',
              returnTo: '',
              path: '',
              subHeader: true,
              bgColor: '',
            },
            meta: { name: 'FAMILY PORTAL', gtm: 'Family Portal' },
          },
          {
            path: '/scholar',
            name: 'Manage Scholar',
            component: lazyLoad(
              'components/familyPortal/manageScholars/scholarContainer/ScholarContainer'
            ),
            children: [
              {
                path: '/scholar',
                redirect: '/scholar/details',
              },
              {
                path: '/scholar/details',
                name: 'Scholar Details',
                component: lazyLoad(
                  'components/familyPortal/manageScholars/scholarContainer/scholarDetails/ScholarDetails'
                ),
                props: {
                  subHeaderTitle: '',
                  returnTo: '',
                  path: '/application/sites',
                  subHeader: true,
                  bgColor: '',
                },
                meta: { name: 'SCHOLAR DETAILS', gtm: 'Scholar Details' },
              },
              {
                path: '/scholar/programs',
                name: 'Scholar Programs',
                component: lazyLoad(
                  'components/familyPortal/manageScholars/scholarContainer/scholarPrograms/ScholarPrograms'
                ),
                props: {
                  subHeaderTitle: '',
                  returnTo: '',
                  path: '',
                  subHeader: true,
                  bgColor: '',
                },
                meta: { name: 'SCHOLAR PROGRAMS', gtm: 'Scholar Programs' },
              },
              {
                path: '/scholar/documents',
                name: 'Scholar Documents',
                component: lazyLoad(
                  'components/familyPortal/manageScholars/scholarContainer/scholarDocuments/ScholarDocuments'
                ),
                props: {
                  subHeaderTitle: '',
                  returnTo: '',
                  path: '/forms',
                  subHeader: true,
                  bgColor: '',
                },
                meta: { name: 'SCHOLAR DOCUMENTS', gtm: 'Scholar Documents' },
              },
            ],
          },
        ],
      },
      //Application paths used solely for the ActivePrograms 'Complete Outstanding Items' link
      {
        path: '/application',
        name: 'Manage Application',
        component: lazyLoad(
          'components/familyPortal/manageApplications/ManageApplication'
        ),
        children: [
          {
            path: '/application',
            redirect: '/application/sites',
          },
          {
            path: '/application/sites',
            name: 'Site/Session List',
            component: lazyLoad(
              'components/familyPortal/manageApplications/sitesListing/SitesListing'
            ),
            props: {
              subHeaderTitle: '',
              returnTo: '',
              path: '/application/sites',
              subHeader: true,
              bgColor: '',
            },
            meta: { name: 'SITE/SESSION LIST', gtm: 'Site/Session List' },
          },
          {
            path: '/application/scholars',
            name: 'Select Scholar',
            component: lazyLoad(
              'components/familyPortal/manageApplications/selectScholar/SelectScholar'
            ),
            props: {
              subHeaderTitle: '',
              returnTo: '',
              path: '/application/scholars',
              subHeader: true,
              bgColor: '',
            },
            meta: { name: 'SELECT SCHOLAR', gtm: 'Select Scholar' },
          },
          {
            path: '/application/forms',
            name: 'Application Forms',
            component: lazyLoad('components/familyPortal/forms/Forms'),
            props: {
              subHeaderTitle: '',
              returnTo: '',
              path: '/forms',
              subHeader: true,
              bgColor: '',
            },
            meta: { name: 'APPLICATION FORMS', gtm: 'Application Forms' },
          },
          {
            path: '/application/upload/documents',
            name: 'Upload Documents',
            component: lazyLoad(
              'components/familyPortal/manageApplications/uploadDocuments/UploadDocuments'
            ),
            props: {
              subHeaderTitle: '',
              returnTo: '',
              path: '/application/upload/documents',
              subHeader: true,
              bgColor: '',
            },
            meta: { name: 'UPLOAD DOCUMENTS', gtm: 'Upload Documents' },
          },
        ],
      },
      {
        path: '/user/profile',
        name: 'User Profile',
        component: lazyLoad(
          'components/familyPortal/userProfileComponent/UserProfileComponent'
        ),
        props: {
          subHeaderTitle: '',
          returnTo: '',
          path: '/user/profile',
          subHeader: true,
          bgColor: '',
        },
        meta: { name: 'My Profile', gtm: 'Profile' },
      },
    ],
  },
  {
    path: APP_CONST.APP_ROUTES.APPLICATION_EMPTY_STATE.path,
    name: APP_CONST.APP_ROUTES.APPLICATION_EMPTY_STATE.name,
    component: ApplicationEmptyState,
    meta: { name: 'ApplicationClosed', gtm: 'ApplicationClosedState' },
  },
  {
    path: APP_CONST.APP_ROUTES.PAGE_NOT_FOUND.path,
    name: APP_CONST.APP_ROUTES.PAGE_NOT_FOUND.name,
    component: ApplicationEmptyState,
    meta: { name: 'Page Not Found', gtm: 'pagenotfound' },
  },
  {
    path: '/nolongeraccess',
    name: 'No Longer Avaliable',
    component: ApplicationEmptyState,
    meta: { name: 'No Longer Access', gtm: 'nolongeraccess' },
  },
];

export default routes;
